import {Injectable} from '@angular/core';

import {forkJoin, Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';

import isEqual from 'lodash-es/isEqual';

import {FormDescriptor, GroupZoneDescriptor, TabSheetDescriptor, ZoneDescriptor,} from './form-generator.interfaces';
import {FormGeneratorUtils} from './form-generator.utils';

import {REQUEST_OPTIONS, ResponseStatusCodes} from '../../app.constants';
import {ApiService} from '../api';
import {ProfileType, ZoneType} from '../employees/contract/contract.interfaces';
import {EmpModelFamilyWithOptions, EntityPaySerie} from '../hire/hire.interface';

const mapToZoneDescriptors = (data: Map<string, GroupZoneDescriptor>) => {
  return Object.keys(data)
    .flatMap((groupKey: string) => Object.values(data[groupKey].zoneDescriptors));
};

export const countries = [
  'australia',
  'belgium',
  'china',
  'denmark',
  'france',
  'germany',
  'hong_kong',
  'hungary',
  'india',
  'ireland',
  'japan',
  'korea',
  'lebanon',
  'luxembourg',
  'monaco',
  'morocco',
  'netherlands',
  'poland',
  'singapore',
  'slovakia',
  'spain',
  'sweden',
  'switzerland',
  'uk',
  'united_arab_emirates',
  'usa'
];


@Injectable()
export class FormDescriptorService {

  constructor(private api: ApiService) {
  }

  public getPaySeries(): Observable<{ data: Array<EntityPaySerie>, transId: string }> {
    return this.api.get('/ga/api/v1/entity/paySerie');
  }

  public getConfidentialityFields(cliRegId: number): Observable<{ statusCode: number; result: ZoneDescriptor[] }> {
    return this.api.get(`/ga/api/v1/cliEmp/getConfidentialityFields/${cliRegId}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(response => {
          if (response.status === ResponseStatusCodes.NoContent) {
            return {statusCode: ResponseStatusCodes.NoContent, result: []};
          } else {
            const data = response.body;
            const result = <ZoneDescriptor[]>mapToZoneDescriptors(data)
              .map((zoneDescriptor: ZoneDescriptor) => ({
                ...zoneDescriptor,
                _type: zoneDescriptor.type,
                type: 'LIST',
                isRequired: true
              }));
            return {statusCode: response.status, result};
          }
        })
      );
  }

  public hasWorkflowFields(cliRegId: number, workflowType: string): Observable<boolean> {
    return this.api.silent.get(`/repository/api/v1/workflow/${cliRegId}/form-descriptor`, {
      requestOptions: {
        params: {'workflow-type': workflowType}
      }
    }).pipe(
      map(formDescriptor => !isEqual(formDescriptor, {})),
      catchError(() => of(false))
    );
  }

  public getWorkflowFields(cliRegId: number, workflowType: string): Observable<ZoneDescriptor[]> {
    return this.api.get(`/repository/api/v1/workflow/${cliRegId}/form-descriptor`, {
      requestOptions: {
        params: {'workflow-type': workflowType}
      }
    }).pipe(
      switchMap(formDescriptor => {
        if (!isEqual(formDescriptor, {})) {
          return forkJoin(
            mapToZoneDescriptors(formDescriptor)
              .sort((zoneA: ZoneDescriptor, zoneB: ZoneDescriptor) => zoneA.displayorder < zoneB.displayorder ? -1 : 1)
              .map((zoneDescriptor: ZoneDescriptor) => {
                if ((zoneDescriptor.type === 'ENTITY' || zoneDescriptor.type === 'ENTITY_DATA' || zoneDescriptor.type === 'ENTITY_PREFIXED')
                  && zoneDescriptor.metadatas.rel) {
                  return this.api.get(zoneDescriptor.metadatas.rel).pipe(
                    map(items => (
                      {
                        ...zoneDescriptor,
                        metadatas: {
                          rel: zoneDescriptor.metadatas.rel,
                          items: items.map(item => ({value: item.id, display: item.name}))
                        },
                        _type: zoneDescriptor.type,
                        readOnly: zoneDescriptor.isReadOnly
                      })));
                }
                return of({
                  ...zoneDescriptor,
                  _type: zoneDescriptor.type,
                  readOnly: zoneDescriptor.isReadOnly
                });
              }));
        }
        return of([]);
      })
    );
  }

  public getEmpModels(cliRegId: number): Observable<Array<EmpModelFamilyWithOptions>> {
    return this.api.get(`/repository/api/v1/regulation/${cliRegId}/emp-models`);
  }

  public getEmpModelZoneDescriptors(cliRegId: number): Observable<Map<string, ZoneDescriptor>> {
    return this.getFormDescriptor(cliRegId).pipe(
      switchMap((formDescriptor: FormDescriptor) => {
        const zones = (formDescriptor.tabSheets || [])
          .flatMap((tabSheet: TabSheetDescriptor) => tabSheet.groupZones || [])
          .flatMap((groupZone: GroupZoneDescriptor) => groupZone.zones || []);
        if (!zones.length) {
          return of(new Map<string, ZoneDescriptor>());
        }
        return forkJoin(zones.map((zone: ZoneDescriptor) => this.resolveZoneDescriptorItems(zone)))
          .pipe(map((resolvedZones: ZoneDescriptor[]) =>
            new Map<string, ZoneDescriptor>(resolvedZones.map(zone => [zone.zoneID, zone]))));
      })
    );
  }

  private resolveZoneDescriptorItems(zone: ZoneDescriptor): Observable<ZoneDescriptor> {
    const enrichedZone: ZoneDescriptor = {...zone, _type: zone._type || zone.type};
    const isEntityType = enrichedZone.type === ZoneType.ENTITY
      || enrichedZone.type === ZoneType.ENTITY_DATA
      || enrichedZone.type === ZoneType.ENTITY_PREFIXED;
    if (isEntityType && enrichedZone.metadatas?.rel && !enrichedZone.metadatas?.items) {
      return this.api.get(enrichedZone.metadatas.rel).pipe(
        map(items => ({
          ...enrichedZone,
          metadatas: {
            ...enrichedZone.metadatas,
            items: items.map(item => ({value: item.id, display: item.name}))
          }
        })),
        catchError(() => of(enrichedZone))
      );
    }
    return of(enrichedZone);
  }

  public getFormDescriptorGroupZoneID(cliReg: number, groupZoneID: string, profileType?: ProfileType): Observable<FormDescriptor> {
    const url = profileType
      ? this.api.get(`/repository/api/v1/mySpace/${cliReg}/form-descriptor?profile-type=MSS`)
        .pipe(
          map(response => {
            const result = response;
            result.tabSheets.forEach(tab => {
              tab.groupZones = tab.groupZones.filter(group => group.groupZoneID === groupZoneID);
            });
            return <FormDescriptor>result;
          })
        )
      : this.api.get(`/repository/api/v1/cliReg/${cliReg}/employeeFormDescriptor?groupZoneID=${groupZoneID}`);
    return url
      .pipe(
        map(response => {
          const result = response;
          result.tabSheets = (result.tabSheets || []).filter((tabSheet: TabSheetDescriptor) => {
            return tabSheet.groupZones.length;
          });

          return <FormDescriptor>result;
        })
      );
  }

  /**
   * Get an employee form descriptor for a cliReg.
   *
   * @param cliReg the cliReg to get the form descriptor for
   * @param profileType profile type of form descriptor
   * @returns {Observable<FormDescriptor>} an observable that emits the form descriptor as fetched from the
   * server
   */
  public getFormDescriptor(cliReg: number, profileType?: 'MSS' | 'ESS'): Observable<FormDescriptor> {
    return this.api.get(profileType ?
      `/repository/api/v1/mySpace/${cliReg}/form-descriptor?profile-type=${profileType}` :
      `/repository/api/v1/cliReg/${cliReg}/employeeFormDescriptor`)
      .pipe(
        map(response => {
          const result = response;
          result.tabSheets = result.tabSheets
            .filter((tabSheet: TabSheetDescriptor) => {
              return tabSheet.groupZones.length;
            })
            .map((tabSheet: TabSheetDescriptor) => {
              tabSheet.groupZones.forEach((gz: GroupZoneDescriptor) => {
                gz.zones.forEach((z: ZoneDescriptor) => {
                  if (z.type === 'WORK_CYCLE_TABLE') {
                    z.nbColumns = 4;
                    z.displayorder = 200;
                  }
                  z._isRequired = z.isRequired;
                });
              });

              return tabSheet;
            });

          return <FormDescriptor>result;
        })
      );
  }

  public getEmployeeSkillsFormDescriptor(cliReg: number): Observable<FormDescriptor> {
    return this.api.get(`/repository/api/v1/cliReg/${cliReg}/employee-skills-form-descriptor`)
      .pipe(
        map(response => {
          const result = response;
          result.tabSheets = result.tabSheets
            .filter((tabSheet: TabSheetDescriptor) => {
              return tabSheet.groupZones.length;
            })
            .map((tabSheet: TabSheetDescriptor) => {
              tabSheet.groupZones.forEach((gz: GroupZoneDescriptor) => {
                gz.zones.forEach((z: ZoneDescriptor) => {
                  z._isRequired = z.isRequired;
                });
              });

              return tabSheet;
            });

          return <FormDescriptor>result;
        })
      );
  }

  public getOnboardingFormDescriptor(cliReg: number): Observable<FormDescriptor> {
    return this.api.get(`/repository/api/v1/cliReg/${cliReg}/onboardingFormDescriptor`)
      .pipe(
        map(response => {
          const result = response;
          result.tabSheets = (result.tabSheets || []).filter((tabSheet: TabSheetDescriptor) => {
            return tabSheet.groupZones.length;
          });

          return <FormDescriptor>result;
        })
      );
  }

  public getNewOnboardingFormDescriptor(cliRegId: number): Observable<FormDescriptor> {
    return this.api.get(`/repository/api/v1/workflow/${cliRegId}/emp-form-descriptor?workflow-type=ONBOARDING`)
      .pipe(
        map(response => {
          const result = response;
          result.tabSheets = (result.tabSheets || []).filter((tabSheet: TabSheetDescriptor) => {
            return tabSheet.groupZones.length;
          });

          return <FormDescriptor>result;
        })
      );
  }

  public getFormDescriptorContractID(contractId: number, cliRegId: number, profileType: ProfileType): Observable<FormDescriptor> {

    return this.api.get(`/repository/api/v1/mySpace/${cliRegId}/form-descriptor?profile-type=${profileType}`)
      .pipe(
        map(response => {
          const descriptor: FormDescriptor = response;
          descriptor.tabSheets = descriptor.tabSheets
            .filter((tabSheet: TabSheetDescriptor) => tabSheet.groupZones.length)
            .sort((a: TabSheetDescriptor, b: TabSheetDescriptor) => a.tabSheetPosition - b.tabSheetPosition)
            .map((tabSheet: TabSheetDescriptor) => {
              tabSheet.groupZones
                .sort((a: GroupZoneDescriptor, b: GroupZoneDescriptor) => a.displayorder - b.displayorder)
                .forEach((groupZone: GroupZoneDescriptor) => {
                  if (groupZone.groupZoneID.includes(':')) {
                    groupZone.transId = groupZone.groupZoneID.includes('_')
                      ? groupZone.groupZoneID.split('_')[0]
                      : groupZone.groupZoneID;
                  }

                  groupZone.zones.forEach((zone: ZoneDescriptor) => {
                    if (zone.type === ZoneType.FILE) {
                      if (!zone.metadatas) {
                        zone.metadatas = {};
                      }
                      zone.metadatas.contractId = contractId;
                    }
                  });
                });
              return tabSheet;
            });

          if (FormGeneratorUtils.isFileZoneExist(descriptor)) {
            FormGeneratorUtils.addAttachmentsTab(descriptor.tabSheets);
          }
          return descriptor;
        })
      );
  }


}
